import * as React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

const FORMSPREE_ID = 'mrbydzwy';
const ACTION_URL = `https://formspree.io/f/${FORMSPREE_ID}`;

const formSchema = z.object({
  name: z.string()
    .trim()
    .min(2, { message: "Name must be at least 2 characters" })
    .max(100, { message: "Name must be less than 100 characters" }),
  email: z.string()
    .trim()
    .email({ message: "Invalid email address" })
    .max(255, { message: "Email must be less than 255 characters" }),
  phone: z.string()
    .trim()
    .min(10, { message: "Phone number must be at least 10 characters" })
    .max(20, { message: "Phone number must be less than 20 characters" }),
  message: z.string()
    .trim()
    .max(2000, { message: "Message must be less than 2000 characters" })
    .optional(),
});

export default function ContactForm() {
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = React.useState('');
  const [date, setDate] = React.useState<Date>();
  const { toast } = useToast();

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('loading');
    setError('');

    const form = e.currentTarget;
    const fd = new FormData(form);

    // Honeypot: if filled, treat as spam but pretend success
    if ((fd.get('company') as string)?.trim()) {
      setStatus('success');
      form.reset();
      toast({
        title: "Message Sent",
        description: "Thanks! Your message has been sent.",
      });
      return;
    }

    // Validate form data
    try {
      formSchema.parse({
        name: fd.get('name'),
        email: fd.get('email'),
        phone: fd.get('phone'),
        message: fd.get('message'),
      });
    } catch (validationError) {
      if (validationError instanceof z.ZodError) {
        const firstError = validationError.errors[0];
        setError(firstError.message);
        setStatus('error');
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: firstError.message,
        });
        return;
      }
    }

    try {
      const res = await fetch(ACTION_URL, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd,
      });

      if (res.ok) {
        setStatus('success');
        form.reset();
        toast({
          title: "Message Sent",
          description: "Thanks! Your message has been sent. We'll get back to you within 24 hours.",
        });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        const data = await res.json().catch(() => ({}));
        const errorMsg = data?.errors?.[0]?.message || 'Something went wrong. Please try again.';
        setError(errorMsg);
        setStatus('error');
        toast({
          variant: "destructive",
          title: "Error",
          description: errorMsg,
        });
      }
    } catch {
      const errorMsg = 'Network error. Please try again.';
      setError(errorMsg);
      setStatus('error');
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMsg,
      });
    }
  }

  return (
    <>
      <form onSubmit={onSubmit} className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="name" className="text-sm font-medium text-foreground">
          Name <span className="text-destructive">*</span>
        </label>
        <Input 
          id="name" 
          name="name"
          placeholder="John Doe" 
          className="bg-secondary border-border"
          required
          disabled={status === 'loading'}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium text-foreground">
          Email <span className="text-destructive">*</span>
        </label>
        <Input 
          id="email" 
          name="email"
          type="email" 
          placeholder="john@example.com" 
          className="bg-secondary border-border"
          required
          disabled={status === 'loading'}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="phone" className="text-sm font-medium text-foreground">
          Phone Number <span className="text-destructive">*</span>
        </label>
        <Input 
          id="phone" 
          name="phone"
          type="tel" 
          placeholder="+1 (210) 296-7409" 
          className="bg-secondary border-border"
          required
          disabled={status === 'loading'}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="service" className="text-sm font-medium text-foreground">
          Service Type <span className="text-destructive">*</span>
        </label>
        <select 
          id="service" 
          name="service"
          className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-foreground focus:ring-2 focus:ring-primary disabled:opacity-50"
          required
          disabled={status === 'loading'}
        >
          <option value="">Select a service</option>
          <option value="airport">SAT Airport Transportation</option>
          <option value="corporate">Corporate Travel</option>
          <option value="wedding">Wedding</option>
          <option value="wine-tours">Wine Tours</option>
          <option value="special-events">Special Events</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Date of Service
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal bg-secondary border-border",
                  !date && "text-muted-foreground"
                )}
                disabled={status === 'loading'}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          <input type="hidden" name="service-date" value={date ? format(date, "yyyy-MM-dd") : ""} />
        </div>

        <div className="space-y-2">
          <label htmlFor="time" className="text-sm font-medium text-foreground">
            Time of Service
          </label>
          <Input 
            id="time" 
            name="service-time"
            type="time" 
            className="bg-secondary border-border"
            disabled={status === 'loading'}
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="pickup" className="text-sm font-medium text-foreground">
          Pickup Location
        </label>
        <Input 
          id="pickup" 
          name="pickup-location"
          placeholder="e.g., 123 Main St, San Antonio, TX" 
          className="bg-secondary border-border"
          disabled={status === 'loading'}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="destination" className="text-sm font-medium text-foreground">
          Destination
        </label>
        <Input 
          id="destination" 
          name="destination"
          placeholder="e.g., San Antonio International Airport" 
          className="bg-secondary border-border"
          disabled={status === 'loading'}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="passengers" className="text-sm font-medium text-foreground">
          Number of Passengers
        </label>
        <select 
          id="passengers" 
          name="passengers"
          className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-foreground focus:ring-2 focus:ring-primary disabled:opacity-50"
          disabled={status === 'loading'}
        >
          <option value="">Select number</option>
          <option value="1">1 passenger</option>
          <option value="2">2 passengers</option>
          <option value="3">3 passengers</option>
          <option value="4">4 passengers</option>
          <option value="5">5 passengers</option>
          <option value="6">6 passengers</option>
          <option value="7+">7+ passengers</option>
        </select>
      </div>

      <div className="space-y-2">
        <label htmlFor="message" className="text-sm font-medium text-foreground">
          Message / Special Requests
        </label>
        <Textarea 
          id="message" 
          name="message"
          placeholder="Tell us about your transportation needs, special requests, flight numbers, etc..." 
          rows={5} 
          className="bg-secondary border-border"
          disabled={status === 'loading'}
        />
      </div>

      {/* Honeypot field (hidden from users) */}
      <input
        type="text"
        name="company"
        autoComplete="off"
        tabIndex={-1}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
        aria-hidden="true"
      />

      <Button 
        type="submit" 
        className="w-full btn-luxury text-lg py-6"
        disabled={status === 'loading'}
      >
        {status === 'loading' ? 'Sending Request...' : 'Request Quote'}
      </Button>

      {status === 'success' && (
        <p className="text-sm text-green-600 dark:text-green-400 text-center">
          ✓ Thanks! Your message has been sent.
        </p>
      )}
      {status === 'error' && error && (
        <p className="text-sm text-destructive text-center">
          {error}
        </p>
      )}
      </form>

      {/*
        ==========================================
        PURE HTML FALLBACK (NO JAVASCRIPT)
        ==========================================
        Uncomment below to use a pure HTML form that works without JavaScript.
        
        <form action="https://formspree.io/f/mrbydzwy" method="POST" className="space-y-6">
          <input type="hidden" name="_subject" value="New Contact Form Submission - Rick's Black Car Service" />
          
          <div className="space-y-2">
            <label htmlFor="name-fallback" className="text-sm font-medium text-foreground">
              Name <span className="text-destructive">*</span>
            </label>
            <input 
              id="name-fallback" 
              name="name"
              type="text"
              placeholder="John Doe" 
              className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-foreground"
              required
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="email-fallback" className="text-sm font-medium text-foreground">
              Email <span className="text-destructive">*</span>
            </label>
            <input 
              id="email-fallback" 
              name="email"
              type="email" 
              placeholder="john@example.com" 
              className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-foreground"
              required
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="phone-fallback" className="text-sm font-medium text-foreground">
              Phone Number
            </label>
            <input 
              id="phone-fallback" 
              name="phone"
              type="tel" 
              placeholder="+1 (555) 123-4567" 
              className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-foreground"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="service-fallback" className="text-sm font-medium text-foreground">
              Service Type
            </label>
            <select 
              id="service-fallback" 
              name="service"
              className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-foreground"
            >
              <option value="">Select a service</option>
              <option value="airport">SAT Airport Transfer</option>
              <option value="corporate">Corporate Travel</option>
              <option value="special">Special Events</option>
              <option value="tour">City Tours</option>
              <option value="hourly">Hourly Charter</option>
              <option value="vip">VIP Services</option>
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="message-fallback" className="text-sm font-medium text-foreground">
              Message <span className="text-destructive">*</span>
            </label>
            <textarea 
              id="message-fallback" 
              name="message"
              placeholder="Tell us about your transportation needs..." 
              rows={5} 
              className="w-full px-3 py-2 rounded-md bg-secondary border border-border text-foreground"
              required
            />
          </div>

          <input
            type="text"
            name="company"
            autoComplete="off"
            tabIndex={-1}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
            aria-hidden="true"
          />

          <button 
            type="submit" 
            className="w-full btn-luxury text-lg py-6"
          >
            Send Message
          </button>
        </form>
      */}
    </>
  );
}
